import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Plus, Search, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Layout } from '@/components/layout'
import { useToast } from '@/lib/hooks/use-toast'
import { useHasPermission } from '@/lib/hooks/usePermission'
import { getRoles, createRole, deleteRole } from '@/lib/services/rbacService'
import { RoleCard } from './RoleCard'
import { RoleFormDialog } from './RoleFormDialog'
import type { RoleListItem } from '@/lib/types/api'
import type { CreateRoleData } from '@/lib/services/rbacService'

export default function RolesListPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [searchQuery, setSearchQuery] = useState('')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [roleToDelete, setRoleToDelete] = useState<RoleListItem | null>(null)

  // Permission checks
  const canViewRoles = useHasPermission('rbac.roles.view')
  const canManageRoles = useHasPermission('rbac.roles.manage')

  // Fetch roles
  const { data, isLoading, error } = useQuery({
    queryKey: ['roles', searchQuery],
    queryFn: () => getRoles(searchQuery ? { search: searchQuery } : undefined),
    enabled: canViewRoles,
  })

  // Create role mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateRoleData) => createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      toast({
        title: t('rbac.roleCreated'),
        description: t('rbac.roleCreatedSuccess'),
      })
      setIsCreateDialogOpen(false)
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: error.message || t('rbac.roleCreateError'),
      })
    },
  })

  // Delete role mutation
  const deleteMutation = useMutation({
    mutationFn: (roleId: string) => deleteRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      toast({
        title: t('rbac.roleDeleted'),
        description: t('rbac.roleDeletedSuccess'),
      })
      setRoleToDelete(null)
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: error.message || t('rbac.roleDeleteError'),
      })
    },
  })

  const handleCreateRole = async (formData: {
    name: string
    slug: string
    description: string
  }) => {
    await createMutation.mutateAsync({
      name: formData.name,
      slug: formData.slug,
      description: formData.description,
    })
  }

  const handleEditRole = (role: RoleListItem) => {
    navigate({ to: `/admin/rbac/roles/${role.id}` })
  }

  const confirmDelete = async () => {
    if (roleToDelete) {
      await deleteMutation.mutateAsync(roleToDelete.id)
    }
  }

  if (!canViewRoles) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">{t('common.accessDenied')}</h1>
            <p className="text-muted-foreground">{t('rbac.noPermissionToViewRoles')}</p>
          </div>
        </div>
      </Layout>
    )
  }

  const roles = data?.roles ?? []

  return (
    <Layout>
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">{t('rbac.rolesAndPermissions')}</h1>
            <p className="text-muted-foreground mt-1">{t('rbac.manageRolesDescription')}</p>
          </div>
          {canManageRoles && (
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('rbac.createRole')}
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md bg-white">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              placeholder={t('rbac.searchRoles')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
              aria-label={t('rbac.searchRoles')}
            />
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
            <span className="sr-only">{t('common.loading')}</span>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="text-center py-12">
            <p className="text-destructive">{t('common.errorLoadingData')}</p>
            <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
          </div>
        )}

        {/* Role cards */}
        {!isLoading && !error && (
          <>
            {roles.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  {searchQuery ? t('rbac.noRolesFound') : t('rbac.noRolesYet')}
                </p>
                {canManageRoles && !searchQuery && (
                  <Button
                    variant="outline"
                    onClick={() => setIsCreateDialogOpen(true)}
                    className="mt-4"
                  >
                    <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t('rbac.createFirstRole')}
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {roles.map(role => (
                  <div
                    key={role.id}
                    onClick={() => handleEditRole(role)}
                    className="cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleEditRole(role)
                      }
                    }}
                    aria-label={t('rbac.viewRoleDetails', { roleName: role.name })}
                  >
                    <RoleCard role={role} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Create Role Dialog */}
        <RoleFormDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          onSubmit={handleCreateRole}
          mode="create"
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!roleToDelete} onOpenChange={() => setRoleToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('rbac.deleteRoleConfirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('rbac.deleteRoleConfirmMessage', { roleName: roleToDelete?.name })}
                {roleToDelete && roleToDelete.userCount > 0 && (
                  <p className="mt-2 text-destructive font-medium">
                    {t('rbac.roleHasUsers', { count: roleToDelete.userCount })}
                  </p>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-destructive hover:bg-destructive/90"
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  )
}
